import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { DashboardHeaderComponent } from '../components/dashboard-header/dashboard-header.component';
import { MobileBottomNavComponent } from '../components/mobile-bottom-nav/mobile-bottom-nav.component';
import { Store } from '@ngrx/store';
import {
  selectAuthLoading,
  selectAuthStatus,
} from '../../store/auth/auth.selectors';
import { LoadingOverlayComponent } from '../../shared/components/loading-overlay/loading-overlay.component';
import { map } from 'rxjs/operators';
import { MediaLibraryDialogComponent } from '../../shared/components/media-library-dialog/media-library-dialog.component';
import {
  haspartnerWorkspaceAccessPermission,
  hasOfferReadPermission,
  hasPropertyCreatePermission,
  hasPropertyReadPermission,
  hasSalesTeamWorkspaceAccessPermission,
  permissionSetFromRaw,
} from '../../shared/utils/permissions.util';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    DashboardHeaderComponent,
    MobileBottomNavComponent,
    LoadingOverlayComponent,
    MediaLibraryDialogComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent {
  private store = inject(Store);
  loading$ = this.store.select(selectAuthLoading);
  navigation$ = this.store
    .select(selectAuthStatus)
    .pipe(map(({ actor, user }) => this.buildNav(actor, user)));
  mobileNavOpen = false;

  private buildNav(actor: any, user: any) {
    const roleCode = String(actor?.role_code || '').trim().toLowerCase();
    const codesRaw = user?.permission_codes ?? user?.permissionCodes ?? [];
    const perms = permissionSetFromRaw(codesRaw);
    const canReadProjects =
      perms.has('projects:read') || perms.has('projects:write');
    const canWriteProjects = perms.has('projects:write');
    const canReadProperties = hasPropertyReadPermission(perms);
    const canSeePortfolio =
      perms.has('portfolio:read') ||
      perms.has('portfolio.total:read') ||
      perms.has('portfolio.subpartners:read') ||
      perms.has('portfolio.sales:read') ||
      perms.has('workspace.portfolio:read') ||
      perms.has('workspace.commission:read');

    type NavChild = { name: string; href: string; icon: string };
    type NavItem = {
      name: string;
      href?: string;
      icon?: string;
      children?: NavChild[];
      collapsible?: boolean;
    };
    const base: NavItem[] = [];
    // 2026-09-13: the dedicated Assignments page is hidden (user request).
    // Assignees still reach every assigned lead through Manage Leads, whose
    // list scope includes the lead_assignments handout arm; partner-role
    // users could never load this page (the endpoint 403s non-subpartners).
    if (canSeePortfolio) {
      base.push({
        name: 'Portfolio',
        href: '/developer/dashboard',
        icon: 'layout-dashboard',
      });
    }
    if (canReadProjects) {
      base.push({
        name: 'Projects',
        href: '/developer/projects',
        icon: 'building-2',
      });
    }
    if (canReadProperties) {
      base.push({
        name: 'Properties',
        href: '/developer/properties',
        icon: 'home',
      });
    }

    // "More" dropdown: relocates the developer dashboard Quick Actions into the
    // sidebar. Each entry mirrors the dashboard quickActions permission gates so
    // the two surfaces stay in sync.
    const moreChildren: NavChild[] = [];
    if (perms.has('reservations:read') || perms.has('reservations:write')) {
      moreChildren.push({
        name: 'Reservations',
        href: '/developer/reservations',
        icon: 'calendar-check',
      });
      moreChildren.push({
        name: 'Manage Leads',
        href: '/developer/leads',
        icon: 'users',
      });
    }
    // Reservation hold/extension config lives on its own page, outside the
    // offers area — the workflow is separate from offer approvals.
    if (canWriteProjects) {
      moreChildren.push({
        name: 'Reservation settings',
        href: '/developer/reservations/settings',
        icon: 'settings-2',
      });
    }
    if (haspartnerWorkspaceAccessPermission(perms)) {
      moreChildren.push({
        name: 'Manage Partners',
        href: '/developer/partners',
        icon: 'user-plus',
      });
    }
    if (hasSalesTeamWorkspaceAccessPermission(perms)) {
      moreChildren.push({
        name: 'Manage Team',
        href: '/developer/sales-team',
        icon: 'users',
      });
    }
    if (hasOfferReadPermission(perms)) {
      moreChildren.push({
        name: 'Offers',
        href: '/developer/offers',
        icon: 'file-text',
      });
      moreChildren.push({
        name: 'Approvals',
        href: '/developer/offers/approvals',
        // badge-check reads as "approvals"; the old triangle-alert read as a
        // warning and clashed with Offers' document icon beside it.
        icon: 'badge-check',
      });
    }
    if (canWriteProjects) {
      moreChildren.push({
        name: 'Add New Project',
        href: '/developer/projects/new',
        icon: 'plus-circle',
      });
    }
    if (hasPropertyCreatePermission(perms)) {
      moreChildren.push({
        name: 'Upload Property',
        href: '/developer/properties/create',
        icon: 'upload',
      });
    }
    if (perms.has('org.settings:read') || perms.has('org.settings:write')) {
      moreChildren.push({
        name: 'Settings',
        href: '/developer/settings',
        icon: 'settings',
      });
    }
    if (moreChildren.length) {
      base.push({
        name: 'More',
        icon: 'ellipsis',
        collapsible: true,
        children: moreChildren,
      });
    }

    return base;
  }
}
